import { useState, useEffect, useRef, createElement } from "react";
import { toast } from "sonner";
import { ExportProgressToast } from "../components/ExportProgressToast";
import { showLegacyRouteStub } from "@/shared/utils/legacyRouteStubs";

export type ExportProgress = {
  status: "idle" | "exporting" | "done";
  current: number;
  total: number;
  exportedIds: number[];
  failedEntries: { id: number; error: string }[];
};

const IDLE: ExportProgress = {
  status: "idle",
  current: 0,
  total: 0,
  exportedIds: [],
  failedEntries: [],
};

type Options = {
  makeApiRequest: (opts: {
    endpoint: string;
    method: 'GET' | 'POST' | 'PUT' | 'DELETE';
    body: unknown;
  }) => Promise<{ data?: any; error?: any }>;
  onComplete?: (result: {
    exportedIds: number[];
    failedEntries: { id: number; error: string }[];
  }) => void;
  progressToastId?: string;
};

const EMPTY_SET = new Set<number>();

export function useExportEntries({ onComplete, progressToastId = "export-progress" }: Options) {
  const [progress, setProgress] = useState<ExportProgress>(IDLE);
  const [exportingIds, setExportingIds] = useState<Set<number>>(EMPTY_SET);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  // Progress toast
  useEffect(() => {
    if (progress.status === "exporting") {
      toast.custom(
        () =>
          createElement(ExportProgressToast, {
            current: progress.current,
            total: progress.total,
          }),
        {
          id: progressToastId,
          duration: Infinity,
          unstyled: true,
          className: "!backdrop-blur-none !bg-transparent !border-none !shadow-none !p-0",
          style: { zoom: 1.1 },
        },
      );
    } else {
      toast.dismiss(progressToastId);
    }
  }, [progress.status, progress.current, progress.total, progressToastId]);

  const exportEntries = async (ids: number[]) => {
    if (progress.status === "exporting") return;
    if (ids.length === 0) return;

    showLegacyRouteStub("/records/exportRecords", "/v2/entries/export");
    const failedEntries = ids.map((id) => ({
      id,
      error: "Needs to be wired to /v2/entries/export",
    }));
    setExportingIds(EMPTY_SET);
    setProgress({
      status: "done",
      current: ids.length,
      total: ids.length,
      exportedIds: [],
      failedEntries,
    });
    onCompleteRef.current?.({ exportedIds: [], failedEntries });
  };

  const reset = () => setProgress(IDLE);

  return {
    progress,
    exportEntries,
    exportingIds,
    reset,
    isExporting: progress.status === "exporting",
  };
}
